import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';
import { useSelector } from 'react-redux';

import { getCurrentPullRequest } from 'src/redux/pull-request/selectors';
import { MergeCheck, MergeCheckKey } from 'src/types';

import { ClassicMergeCheckName } from '../classic-merge-check-name';
import { classicCheckMessages, messages as commonMessages } from '../i18n';
import { MergeResultsPopupMouseCallbacks } from '../merge-checks-lists/rich-merge-checks-list/hooks';

import { messages } from './i18n';
import { MergeCheckItem } from './merge-check-item';

type ClassicMergeCheckItemProps = {
  mergeCheckKey: string;
  check: MergeCheck;
  // Below props are only required to show the Popup on hover.
  // The useMergeResultsPopupProps hook can provide these.
  popupIsOpen?: boolean;
  showPopup?: (key?: string) => void;
  hidePopup?: () => void;
  mouseCallbacks?: MergeResultsPopupMouseCallbacks;
};

export const ClassicMergeCheckItem = ({
  mergeCheckKey,
  check,
  popupIsOpen,
  showPopup,
  hidePopup,
  mouseCallbacks,
}: ClassicMergeCheckItemProps) => {
  const { formatMessage } = useIntl();
  const pr = useSelector(getCurrentPullRequest);

  const branchName = useMemo(
    () =>
      pr?.destination.branch.name ?? formatMessage(messages.unknownBranchLabel),
    [formatMessage, pr?.destination.branch.name]
  );

  const description = useMemo(
    () =>
      !check.pass ? formatMessage(classicCheckMessages[check.key].error) : null,
    [formatMessage, check.pass, check.key]
  );

  const nameAriaLabel = useMemo<string>(() => {
    const label = check.count
      ? `${check.count}${
          check.key === MergeCheckKey.MAXIMUM_COMMITS_BEHIND ? '' : '+'
        }`
      : formatMessage(
          check.key === MergeCheckKey.FAILED_BUILDS ||
            check.key === MergeCheckKey.IN_PROGRESS_BUILDS
            ? commonMessages.noLabel
            : commonMessages.allLabel
        );

    return formatMessage(classicCheckMessages[check.key].name, {
      count: check.count,
      label,
    });
  }, [check.count, check.key, formatMessage]);

  return (
    <div data-testid={`classicMergeCheckItem-${check.key}`}>
      <MergeCheckItem
        mergeCheckKey={mergeCheckKey}
        status={check.pass ? 'SUCCESS' : 'FAILED'}
        name={<ClassicMergeCheckName check={check} />}
        nameAriaLabel={nameAriaLabel}
        description={description}
        updatedOn={null}
        branchName={branchName}
        popupIsOpen={popupIsOpen}
        showPopup={showPopup}
        hidePopup={hidePopup}
        mouseCallbacks={mouseCallbacks}
        required={check.allow_merge === false}
      />
    </div>
  );
};
